/**
 * Onboarding progress, split by who the steps actually belong to (owner, Oct 2026).
 *
 * Practitioners are judged on the six practitioner steps, storefront owners on the four vendor
 * steps. Anyone who is both appears under Practitioners only, where a storefront is already an
 * optional step on their list.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Users, Store, Mail } from 'lucide-react';
import AdminSetupProgressList from './AdminSetupProgressList';
import AdminVendorInterest from './AdminVendorInterest';

const AUDIENCES = [
  {
    key: 'interest',
    label: 'Wants to sell',
    icon: Mail,
    note: 'People who asked through Sell Here. They have the agreements and the questionnaire; invite them when you are ready.',
  },
  {
    key: 'practitioners',
    label: 'Practitioners',
    icon: Users,
    endpoint: '/admin/practitioners/setup-progress',
    itemsKey: 'practitioners',
    title: 'Practitioner onboarding',
    emptyLabel: 'No practitioners yet.',
    testId: 'admin-progress',
    note: 'Signed agreement, practice profile, sessions, availability, bookings on, bank connected.',
  },
  {
    key: 'vendors',
    label: 'Vendors only',
    icon: Store,
    endpoint: '/admin/vendors/setup-progress',
    itemsKey: 'vendors',
    title: 'Vendor onboarding',
    emptyLabel: 'No storefront owners yet.',
    testId: 'admin-vendor-progress',
    note: 'Storefront, first product, ship-from address, bank connected. Practitioners who also sell are on the Practitioners list.',
  },
];

export default function AdminOnboardingTab() {
  const [active, setActive] = useState('practitioners');
  const audience = AUDIENCES.find((a) => a.key === active);

  return (
    <div className="space-y-4" data-testid="admin-onboarding-tab">
      <div className="flex flex-wrap items-center gap-2">
        {AUDIENCES.map(({ key, label, icon: Icon }) => (
          <Button
            key={key}
            size="sm"
            variant="ghost"
            onClick={() => setActive(key)}
            className={`h-9 gap-2 rounded-full border px-4 text-xs transition-colors ${
              active === key
                ? 'border-cyan-400/40 bg-cyan-500/15 text-cyan-100'
                : 'border-white/10 bg-white/[0.02] text-gray-300 hover:text-white hover:bg-white/5'
            }`}
            data-testid={`admin-onboarding-switch-${key}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </Button>
        ))}
      </div>

      <p className="text-xs text-gray-400" data-testid="admin-onboarding-note">{audience.note}</p>

      {active === 'interest' ? (
        <AdminVendorInterest />
      ) : (
        <AdminSetupProgressList
          key={audience.key}
          endpoint={audience.endpoint}
          itemsKey={audience.itemsKey}
          title={audience.title}
          emptyLabel={audience.emptyLabel}
          testId={audience.testId}
        />
      )}
    </div>
  );
}
